import service from '@/api/notification';
import {handleError, loading} from "../generators/helpers";
import {toast} from 'vue3-toastify'

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"]/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'
}[c]))

// Reusable: ring the notification sound, autoplay-safe. play() returns a Promise
// that REJECTS when the browser blocks autoplay before any user interaction — that
// rejection isn't caught by try/catch, so it must be swallowed explicitly. Exported
// so any other part/module (e.g. a chat) can ring the same sound.
export const playNotificationSound = () => {
    try {
        const sound = new Audio(require('@/assets/audio/notification.mp3')).play()
        if (sound && typeof sound.catch === 'function') {
            sound.catch(() => { /* autoplay blocked until the user interacts — ignore */ })
        }
    } catch (e) { /* asset/decoding failure — ignore */ }
}

// Reusable: show the in-app toast for a chat / internal message (title + sender, then
// a body excerpt; click opens the reply popup). Reusable by any chat-style notifier.
export const showChatToast = (notification) => {
    const data = notification.data || notification
    const from = data.from ? ` — ${escapeHtml(data.from)}` : ''
    toast(
        `<strong>${escapeHtml(data.title)}</strong>${from}<br>` +
        `<span class="small">${escapeHtml(data.message)}</span>`,
        {
            dangerouslyHTMLString: true, type: 'info', position: 'top-right', autoClose: 7000,
            // Click the toast → open the reply popup (dynamic import avoids the
            // store ↔ module circular dependency at load time).
            onClick: () => import('@/state/store').then(({default: store}) => store.dispatch('notification/openReply', notification))
        }
    )
}

// Handle one freshly-arrived notification — from a live broadcast OR the inbox
// poll (the no-Pusher fallback). Dedup by id, prepend to the list, bump unread,
// play the sound, and for internal messages show a toast (title + sender, then a
// body excerpt + a "check your e-mail" hint).
const receive = (state, notification) => {
    if (!notification || !notification.id) {
        return
    }
    if (!state.list) {
        state.list = []
    }
    if (state.list.some((x) => x.id === notification.id)) {
        return
    }

    state.list.unshift(notification)
    state.unread++
    state.count++

    playNotificationSound()

    const data = notification.data || notification
    if (data.channel === 'internal') {
        showChatToast(notification)
    }
}

const generate = function () {
    return {
        state: {
            loading: false,
            list: [],
            unread: 0,
            errors: {},
            count: 0,
            replyTarget: null,
            service: service('notifications')
        },

        mutations: {
            'SET_LOADING'(state, loading) {
                state.loading = loading
            },

            'SET_LIST'(state, list) {
                state.list = list
            },

            'SET_UNREAD'(state, number) {
                state.unread = number
            },

            'SET_COUNT'(state, number) {
                state.count = number
            },

            'DECREASE_UNREAD'(state) {
                state.unread--
            },

            'INCREASE_UNREAD'(state) {
                state.unread++
            },

            'SET_READ_ALL'(state) {
                state.unread = 0
                if (state.list && state.list.length) {
                    const date = new Date()
                    const padTo2Digits = (num) => {
                        return num.toString().padStart(2, '0')
                    }

                    state.list.forEach(item => {
                        item.read_at = `${date.getFullYear()}-${padTo2Digits(date.getMonth() + 1)}-${padTo2Digits(date.getDate())} ${padTo2Digits(date.getHours())}:${padTo2Digits(date.getMinutes())}:${padTo2Digits(date.getSeconds())}`
                    })
                }
            },

            'SET_ERRORS'(state, errors) {
                state.errors = errors
            },

            'APPEND_ITEMS'(state, items) {
                if (!items || !items.length) {
                    return
                }

                items.forEach(item => {
                    state.list.push(item)
                })
            },

            // Kept compatible with receive(): guard the list, dedup by id, bump the
            // counters, and ring the reusable notification sound on a genuinely new item.
            'PREPEND_LIST'(state, item) {
                if (!state.list) {
                    state.list = []
                }

                if (state.list.some(x => x.id === item.id)) {
                    return
                }

                state.list.unshift(item)
                state.unread++
                state.count++
                playNotificationSound()
            },

            'UPDATE_IN_LIST'(state, id) {
                if (state.list && state.list.length > 0) {
                    const idx = state.list.findIndex(x => x.id === id)
                    const date = new Date()

                    if (idx !== -1) {
                        state.list[idx].read_at = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate() + " " + date.getHours() + ":" + date.getMinutes() + ":" + date.getSeconds()
                    }
                }
            },

            // Used by the poll fallback to inject a freshly-fetched notification.
            'RECEIVE'(state, notification) {
                receive(state, notification)
            },

            'SET_REPLY_TARGET'(state, item) {
                state.replyTarget = item
            },

            'CLEAR_REPLY_TARGET'(state) {
                state.replyTarget = null
            }
        },

        actions: {
            fetch({commit, getters, state, rootGetters}) {
                if (!rootGetters['user/authenticated']?.id || (getters.count === state.count && getters.count && state.count)) {
                    return
                }

                loading(commit)
                return state.service.fetch({offset: getters.count, limit: 5}).then(response => {
                    commit('SET_ERRORS', {});
                    if (!state.list.length) {
                        commit('SET_LIST', response.data)
                    } else {
                        commit('APPEND_ITEMS', response.data)
                    }
                    commit('SET_UNREAD', response.unread)
                    commit('SET_COUNT', response.count)
                    loading(commit, false)
                    return response.data;
                }).catch((e) => {
                    if (e.response?.status === 401) {
                        return;
                    } else {
                        handleError(e, commit);
                    }
                })
            },

            // No-Pusher fallback: periodically fetch the newest notifications and
            // surface any that aren't in the list yet (toast + prepend). Safe to run
            // alongside broadcasting — receive() dedups by id, so nothing doubles.
            poll({state, rootGetters, commit}) {
                // jelszócsere előtt a szerver úgyis 423-mal utasítana el minden lekérést
                if (!rootGetters['user/authenticated']?.id || rootGetters['user/authenticated']?.password_policy?.must_change) {
                    return
                }

                return state.service.fetch({offset: 0, limit: 5}).then(response => {
                    const items = (response && response.data) || []
                    // Oldest-first so the newest ends up on top after the unshifts.
                    items.slice().reverse().forEach(item => {
                        if (!state.list.some(x => x.id === item.id)) {
                            commit('RECEIVE', item)
                        }
                    })
                }).catch(() => {})
            },

            // Open / close the reply popup for an internal message.
            openReply({commit}, item) {
                commit('SET_REPLY_TARGET', item)
            },

            closeReply({commit}) {
                commit('CLEAR_REPLY_TARGET')
            },

            markAsRead({commit}, id) {
                loading(commit)
                return state.service.markAsRead(id).then(response => {
                    commit('SET_ERRORS', {});
                    commit('UPDATE_IN_LIST', id)
                    commit('DECREASE_UNREAD')
                    loading(commit, false)
                    return response.data;
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            markAsReadAll({commit, state}) {
                loading(commit)
                return state.service.markAsReadAll().then(response => {
                    commit('SET_ERRORS', {});
                    commit('SET_READ_ALL');
                    loading(commit, false)
                    return response.data;
                }).catch((e) => {
                    handleError(e, commit)
                })
            }
        },

        getters: {
            list: state => JSON.parse(JSON.stringify(state.list)),
            loading: state => state.loading,
            errors: state => state.errors,
            unread: state => state.unread,
            count: state => state.list.length,
            allLoaded: state => state.count <= state.list.length,
            allRead: state => state.list?.every(noti => noti.read_at),
            replyTarget: state => state.replyTarget,
        },

        namespaced: true,
    }
}

const module = generate()

export const state = module.state
export const getters = module.getters
export const mutations = module.mutations
export const actions = module.actions
